import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { BookOpen, FileText, Download, Eye, Search, Filter, Star, Clock, Video, Headphones, File, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { useEffect, useState } from "react";
import { cn } from "../ui/utils";
import API from "../api/api01";
import { toast } from "sonner";

interface Book {
  id: string;
  title: string;
  author: string;
  category: string;
  isbn: string;
  available: boolean;
  coverImage: string;
  rating: number;
  borrowedUntil?: string;
}

interface LectureMaterial {
  id: string;
  title: string;
  subject: string;
  professor: string;
  type: "PDF" | "PPT" | "DOC";
  size: string;
  uploadDate: string;
  downloads: number;
}

interface DigitalResource {
  id: string;
  title: string;
  type: "Video" | "Audio" | "Interactive";
  duration: string;
  subject: string;
  description: string;
  views: number;
  thumbnail: string;
}

export function Library() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const userFromStorage = JSON.parse(localStorage.getItem("user") || "{}");
  const studentId = userFromStorage?.id;

  useEffect(() => {
    if (studentId) {
      fetchLibraryData();
    } else {
      setLoading(false);
    }
  }, [studentId]);

  const fetchLibraryData = async () => {
    try {
      const response = await API.get(`/superadmin/student/library-data/${studentId}`);
      setData(response.data);
    } catch (error) {
      console.error("Error fetching library data:", error);
      toast.error("Failed to load library resources");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const books = data?.books || [];
  const lectureMaterials = data?.lectureMaterials || [];
  const digitalResources = data?.digitalResources || [];

  const categories = ["all", ...Array.from(new Set(books.map((b: any) => b.category)))];

  const filteredBooks = books.filter((book: any) => {
    const matchesSearch = book.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (book.author && book.author.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = selectedCategory === "all" || book.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "PDF": return <File className="h-4 w-4" />;
      case "PPT": return <FileText className="h-4 w-4" />;
      case "DOC": return <FileText className="h-4 w-4" />;
      case "Video": return <Video className="h-4 w-4" />;
      case "Audio": return <Headphones className="h-4 w-4" />;
      case "Interactive": return <BookOpen className="h-4 w-4" />;
      default: return <File className="h-4 w-4" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "PDF": return "bg-red-500/10 text-red-600 dark:text-red-400";
      case "PPT": return "bg-orange-500/10 text-orange-600 dark:text-orange-400";
      case "DOC": return "bg-blue-500/10 text-blue-600 dark:text-blue-400";
      case "Video": return "bg-purple-500/10 text-purple-600 dark:text-purple-400";
      case "Audio": return "bg-pink-500/10 text-pink-600 dark:text-pink-400";
      case "Interactive": return "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400";
      default: return "bg-gray-500/10 text-gray-600 dark:text-gray-400";
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div>
        <h1>Library</h1>
        <p className="text-muted-foreground mt-1">
          Access books, lecture materials, and digital resources
        </p>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search books, materials, resources..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-lg"
          />
        </div>
        <Button variant="outline" className="rounded-lg">
          <Filter className="h-4 w-4 mr-2" />
          Filters
        </Button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="books" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 lg:w-auto rounded-lg">
          <TabsTrigger value="books" className="rounded-lg">
            <BookOpen className="h-4 w-4 mr-2" />
            Books
          </TabsTrigger>
          <TabsTrigger value="materials" className="rounded-lg">
            <FileText className="h-4 w-4 mr-2" />
            Lecture Materials
          </TabsTrigger>
          <TabsTrigger value="digital" className="rounded-lg">
            <Video className="h-4 w-4 mr-2" />
            Digital Resources
          </TabsTrigger>
        </TabsList>

        {/* Books Tab */}
        <TabsContent value="books" className="space-y-6">
          {/* Category Filter */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {categories.map((category: any) => {
              const isSelected = selectedCategory === category;

              return (
                <Button
                  key={String(category)}
                  variant={isSelected ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(String(category))}
                  className={cn(
                    "rounded-full whitespace-nowrap transition-colors duration-200",
                    isSelected && "text-white font-semibold dark:text-foreground"
                  )}
                >
                  {category === "all" ? "All Categories" : String(category)}
                </Button>
              );
            })}
          </div>
          {/* Books Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBooks.map((book: any) => (
              <Card
                key={book.id}
                className="shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden group"
              >
                <div className="aspect-[3/4] overflow-hidden bg-muted">
                  <img
                    src={book.coverImage}
                    alt={book.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>

                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-base line-clamp-2 text-white">
                        {book.title}
                      </CardTitle>
                      <CardDescription className="mt-1 text-white/80">
                        {book.author}
                      </CardDescription>
                    </div>

                    <div className="flex items-center gap-1 text-amber-400">
                      <Star className="h-4 w-4 fill-current" />
                      <span className="text-sm font-medium text-white">
                        {book.rating}
                      </span>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-white/70">ISBN:</span>
                    <span className="font-medium text-white">
                      {book.isbn}
                    </span>
                  </div>

                  <Badge
                    variant={book.available ? "default" : "secondary"}
                    className="w-full justify-center rounded-lg text-white"
                  >
                    {book.available
                      ? "Available"
                      : `Borrowed until ${book.borrowedUntil}`}
                  </Badge>

                  <div className="flex gap-2">
                    <Button
                      className="flex-1 rounded-lg text-white"
                      disabled={!book.available}
                    >
                      <BookOpen className="h-4 w-4 mr-2 text-white" />
                      Borrow
                    </Button>

                    <Button
                      variant="outline"
                      size="icon"
                      className="rounded-lg text-white"
                    >
                      <Eye className="h-4 w-4 text-white" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Lecture Materials Tab */}
        <TabsContent value="materials" className="space-y-4">
          {lectureMaterials.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground border border-dashed rounded-lg">
              No lecture materials available yet.
            </div>
          ) : (
            lectureMaterials.map((material: any) => (
              <Card key={material.id} className="shadow-sm hover:shadow-md transition-all duration-200">
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row items-start gap-4">
                    <div className={cn("p-3 rounded-lg", getTypeColor(material.type))}>
                      {getTypeIcon(material.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold mb-1">{material.title}</h3>
                      <p className="text-sm text-muted-foreground mb-2">{material.subject} • {material.professor}</p>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {material.uploadDate}
                        </span>
                        <span>{material.size}</span>
                        <span>{material.downloads} downloads</span>
                      </div>
                    </div>
                    <div className="flex gap-2 self-start sm:self-center">
                      <Button
                        size="sm"
                        className="rounded-lg text-white hover:text-white">
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-lg">
                        <Eye className="h-4 w-4 mr-2" />
                        View
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Digital Resources Tab */}
        <TabsContent value="digital" className="space-y-6">
          {digitalResources.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground border border-dashed rounded-lg">
              No digital resources available yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {digitalResources.map((resource: any) => (
                <Card key={resource.id} className="shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden group">
                  <div className="aspect-video overflow-hidden bg-muted relative">
                    <img
                      src={resource.thumbnail}
                      alt={resource.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-3 right-3">
                      <Badge className={cn("rounded-full", getTypeColor(resource.type))}>
                        {getTypeIcon(resource.type)}
                        <span className="ml-1">{resource.type}</span>
                      </Badge>
                    </div>
                    <div className="absolute bottom-3 right-3 bg-black/80 text-white text-xs px-2 py-1 rounded">
                      {resource.duration}
                    </div>
                  </div>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base line-clamp-2">{resource.title}</CardTitle>
                    <CardDescription className="line-clamp-2">{resource.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{resource.subject}</span>
                      <span className="text-muted-foreground">{resource.views} views</span>
                    </div>
                    <Button className="w-full rounded-lg text-white hover:text-white">
                      <Eye className="h-4 w-4 mr-2" />
                      {resource.type === "Interactive" ? "Launch" : "Play"}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="shadow-sm bg-gradient-to-br from-primary/10 to-primary/5">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <BookOpen className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-semibold">{books.filter((b: any) => !b.available).length}</p>
                <p className="text-sm text-muted-foreground">Books Borrowed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm bg-gradient-to-br from-secondary/10 to-secondary/5">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <FileText className="h-8 w-8 text-secondary" />
              <div>
                <p className="text-2xl font-semibold">{lectureMaterials.length}</p>
                <p className="text-sm text-muted-foreground">Lecture Materials</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm bg-gradient-to-br from-chart-3/10 to-chart-3/5">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <Video className="h-8 w-8 text-chart-3" />
              <div>
                <p className="text-2xl font-semibold">{digitalResources.length}</p>
                <p className="text-sm text-muted-foreground">Digital Resources</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}